import React, { useState, useEffect } from 'react';
import { fetchNickname } from '@/hooks/useMemberPermission';
import FooterDrawer from '@/components/ui/atoms/FooterDrawer';
import MInputField from '@/components/ui/atoms/MInputField';
import { getMemberInfo } from '@/hooks/useMembers';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

interface EditNicknameDrawerProps {
  open: boolean;
  member: any;
  onClose: () => void;
  onDone: (nickname: string) => void;
}

const EditNicknameDrawer: React.FC<EditNicknameDrawerProps> = ({ open, member, onClose, onDone }) => {
  const [nickname, setNickname] = useState(member?.nickname || '');
  const [loading, setLoading] = useState(false);
  const [originalNickname, setOriginalNickname] = useState('');
  const memberInfo = getMemberInfo(true);

  // Track GTM user_data_loaded event when drawer opens
  useEffect(() => {
    if (open && memberInfo?.id) {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
        screenName: ANALYTICS_SCREEN_NAMES.FAMILY_INFO_EDIT_NICKNAME,
      });
    }
  }, [open, memberInfo?.id]);

  // Always fetch nickname when drawer is opened (open transitions to true)
  useEffect(() => {
    if (open && member?._id) {
      fetchNickname({
        familyMemberId: member._id,
      }).then(nick => {
        setNickname(nick || '');
        setOriginalNickname((nick || '').trim());
      });
    }
    if (!open) {
      setNickname('');
      setOriginalNickname('');
    }
  }, [open, member?._id]);

  React.useEffect(() => {
    setNickname(member?.nickname || '');
    setOriginalNickname((member?.nickname || '').trim());
  }, [member]);

  const trimmedNickname = nickname.trim();
  const unchanged =
    trimmedNickname.length === 0 ||
    trimmedNickname.localeCompare(originalNickname, undefined, { sensitivity: 'base' }) === 0;

  return (
    <FooterDrawer
      open={open}
      onClose={onClose}
      title="Edit Nickname"
      doneText="Done"
      onDone={() => {
        setLoading(true);
        
        // Track nickname added/updated event
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
          screenName: ANALYTICS_SCREEN_NAMES.FAMILY_INFO_NICKNAME_ADDED,
        });
        
        onDone(trimmedNickname);
      }}
      doneDisabled={loading || unchanged}
      donePosition="top"
      height='45vh'
    >
      <div className="p-4">
        <MInputField
          variant="Underlined"
          showLabel
          label="What's your nickname for them?"
          size="md"
          value={nickname}
          onChange={e => setNickname(e.target.value)}
          autoFocus
          placeholder="Not Answered"
        />
      </div>
    </FooterDrawer>
  );
};

export default EditNicknameDrawer;
